/**
 * ArtifactWordCloud — TELV1-332 (concept-cloud reframe).
 *
 * Renders the `word_cloud` artifact type produced by the Research Assistant.
 * The data carried by this artifact changed from raw word frequencies to
 * distinctive concepts — see the analyzer's ConceptExtractionService for
 * the source of truth. The visualization remains a cloud, sentiment-coloured.
 *
 * Sparse fallback: < 5 concepts renders as a ranked horizontal bar list
 * using the same sentiment palette (Displayr's evidence: bars beat clouds
 * at low counts on every dimension that matters for decisions).
 *
 * Carries the same header chrome as ArtifactChartBlock: title + Download PNG
 * + Copy data, in an `html-to-image`-capturable white card.
 */

import { Copy, Check, Download, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';
import useTranslation from '@/hooks/useTranslation';
import WordCloud, { type WordCloudItem } from '@/components/WordCloud/WordCloud';
import type { ConceptEntry, ConceptSentiment } from '@/reactQuery/get';
import useCopyState from './useCopyState';
import useDownloadImage from './useDownloadImage';

// Mirror the per-question card's threshold so both surfaces decide
// "is this too sparse for a cloud?" the same way.
const SPARSE_CLOUD_THRESHOLD = 5;

const SENTIMENT_BAR_COLORS: Record<ConceptSentiment, string> = {
  positive: '#096846',
  negative: '#B83060',
  mixed: '#7A5060',
  neutral: '#2A3547',
};

interface ArtifactWordCloudProps {
  title: string;
  data: {
    /**
     * Server-supplied concepts. Backend writes this field via the
     * word_cloud id-handoff in research-assistant.service.ts (the model
     * passes only `concepts_id`; the validator populates this).
     */
    concepts?: ConceptEntry[];
    scope?: {
      question_id: string | null;
      filter_id: string | null;
      total_unique_respondents: number;
    };
  };
}

function conceptToWordCloudItem(c: ConceptEntry): WordCloudItem {
  return {
    text: c.phrase,
    value: Math.max(1, c.unique_respondents),
    sentiment: c.sentiment,
    tooltip: `${c.phrase} — ${c.unique_respondents} respondent${
      c.unique_respondents === 1 ? '' : 's'
    } (${c.sentiment})`,
    payload: c,
  };
}

/** Sparse-data fallback (mirrors CategoriesZone's ConceptBarList). */
function ConceptBarList({ concepts, maxValue }: { concepts: ConceptEntry[]; maxValue: number }) {
  return (
    <ul className="flex flex-col gap-2">
      {concepts.map((c) => {
        const pct = Math.max(2, Math.round((c.unique_respondents / maxValue) * 100));
        const color = SENTIMENT_BAR_COLORS[c.sentiment];
        return (
          <li key={c.phrase} className="flex items-center gap-3">
            <span
              className="text-[13px] text-neutral-700 truncate min-w-[6rem] max-w-[40%]"
              title={`${c.phrase} (${c.sentiment})`}
            >
              {c.phrase}
            </span>
            <span className="flex-1 h-3 rounded-sm bg-neutral-100 overflow-hidden">
              <span
                className="block h-full rounded-sm"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </span>
            <span className="text-[12px] tabular-nums text-neutral-500 min-w-[2rem] text-right">
              {c.unique_respondents}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ArtifactWordCloud({ title, data }: ArtifactWordCloudProps) {
  const { lang } = useTranslation();
  const concepts = useMemo(() => data?.concepts ?? [], [data?.concepts]);
  const totalRespondents = data?.scope?.total_unique_respondents ?? 0;
  const isSparse = concepts.length > 0 && concepts.length < SPARSE_CLOUD_THRESHOLD;

  const cloudItems = useMemo(() => concepts.map(conceptToWordCloudItem), [concepts]);

  const [copied, triggerCopied] = useCopyState();
  const cloudCardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, isDone, triggerDownload] = useDownloadImage(cloudCardRef, { title });

  const handleCopy = useCallback(() => {
    // TSV: phrase \t unique_respondents \t sentiment — researcher can paste
    // directly into a spreadsheet or deck.
    const rows = concepts
      .map((c) => `${c.phrase}\t${c.unique_respondents}\t${c.sentiment}`)
      .join('\n');
    const header = 'phrase\tunique_respondents\tsentiment';
    navigator.clipboard
      .writeText(`${title}\n${header}\n${rows}`)
      .then(() => triggerCopied())
      .catch(() => {});
  }, [concepts, title, triggerCopied]);

  return (
    <div className="flex flex-col gap-2">
      {/* Header — matches ArtifactChartBlock layout (Download PNG + Copy). */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={triggerDownload}
            disabled={isDownloading}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.downloadWordCloudPng')}
            aria-label={lang.get('msg.downloadWordCloudPng')}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {isDownloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isDone ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </button>

          <button
            type="button"
            onClick={handleCopy}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.copyArtifact')}
            aria-label={lang.get('msg.copyArtifact')}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* White card — html-to-image capture target. */}
      <div ref={cloudCardRef} className="bg-white border border-neutral-200 rounded-lg p-4">
        {isSparse ? (
          <ConceptBarList
            concepts={concepts}
            maxValue={Math.max(1, ...concepts.map((c) => c.unique_respondents))}
          />
        ) : (
          <WordCloud words={cloudItems} height={260} />
        )}
        {totalRespondents > 0 && (
          <p className="text-[11px] text-neutral-400 text-center mt-2">
            Based on {totalRespondents} respondent
            {totalRespondents === 1 ? '' : 's'}
          </p>
        )}
      </div>
    </div>
  );
}

export default ArtifactWordCloud;
